import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import dummyLogo from '@/assets/images/dummy-logo.png';
import KeyOutcomesTab from './key-outcomes-tab';
import SummaryTab from './summary-tab';
import DetailsTab from './details-tab';
import MediaTab from './media-tab';
import { Icons } from '@/components/icons';
import { useGetProject } from '@/services/projects/queries';
import type { Project, ProjectListItem } from '@/services/types';

type dataPanelProps = {
  data: ProjectListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type TabType = 'summary' | 'details' | 'keyoutcomes' | 'media';

export function ProjectPanel({ data, open, onOpenChange }: dataPanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const { data: projectData, isLoading } = useGetProject(data?.id, {
    enabled: !!data?.id && open // Only fetch when panel is open
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full max-w-md overflow-y-auto p-0 sm:max-w-lg'>
        <div className='flex h-full flex-col'>
          <SheetHeader className='flex flex-row items-start p-4 pb-0'>
            <div className='flex flex-1 items-center gap-3'>
              {projectData?.media?.url ? (
                <div className='relative h-12 w-12 overflow-hidden rounded border'>
                  <img
                    src={projectData?.organisation?.logo ?? dummyLogo}
                    alt={`${projectData?.title} logo`}
                    className='h-full w-full object-cover'
                  />
                </div>
              ) : (
                <div className='flex h-12 w-12 items-center justify-center rounded bg-muted'>
                  {projectData?.organisation?.name?.charAt(0)}
                </div>
              )}
              <div className='w-full'>
                <div className='flex min-w-0 items-start'>
                  <div className='max-w-[75%] truncate whitespace-normal break-words'>
                    <SheetTitle className='line-clamp-1 truncate text-ellipsis text-xl'>
                      {projectData?.organisation?.name}
                    </SheetTitle>
                  </div>
                  {projectData?.organisation?.verified && <Icons.verified />}
                </div>
                {projectData?.organisation?.headOfficeLocation && (
                  <p className='overflow-hidden whitespace-pre-wrap break-words text-sm text-muted-foreground'>
                    {projectData?.organisation?.headOfficeLocation}
                  </p>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className='mt-4 border-b'>
            <div className='flex overflow-x-auto'>
              {['Summary', 'Details', 'Key Outcomes', 'Media'].map((tab) => {
                const tabKey = tab.toLowerCase().replace(/\s+/g, '') as TabType;
                return (
                  <Button
                    key={tab}
                    variant='ghost'
                    className={cn(
                      'rounded-none px-4 py-2 text-sm font-medium',
                      activeTab === tabKey
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground'
                    )}
                    onClick={() => setActiveTab(tabKey)}
                  >
                    {tab}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className='w-full flex-1 overflow-y-auto break-words p-4'>
            {isLoading ? (
              <div className='flex items-center justify-center h-32'>
                <div className='text-sm text-muted-foreground'>Loading project details...</div>
              </div>
            ) : (
              <>
                {activeTab === 'summary' && <SummaryTab data={projectData} />}
                {activeTab === 'details' && <DetailsTab data={projectData as Project} />}
                {/* {activeTab === 'history' && <HistoryTab />} */}
                {activeTab === 'media' && <MediaTab data={projectData} />}
                {activeTab === 'keyoutcomes' && <KeyOutcomesTab data={projectData} />}
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
